import React from 'react';
import { Box, Typography, Grid, Paper, Chip, Divider } from '@mui/material';
import InventoryTransferItemDetails from './InventoryTransferItemDetails';
import AuditInformation from '../AuditInformation';
import ExportDetails from '../ExportDetails';
import { getZoneNameFromZone } from '../../catalog/utils/inventory/utils';
import { InventoryTransfer } from '../../../services/inventory/types';

interface InventoryTransferDetailsProps {
  transfer: InventoryTransfer;
}

const InventoryTransferDetails: React.FC<InventoryTransferDetailsProps> = ({ transfer }) => {
  const getTotalQuantityTransferred = () => {
    return transfer.inventoryTransferItems.reduce(
      (total, item) => total + item.quantityTransferred,
      0,
    );
  };

  const getTransferTypeColor = (transferType: string) => {
    return transferType === 'zone' ? 'primary' : 'secondary';
  };

  return (
    <Box sx={{ p: 3 }}>
      <Grid container spacing={3}>
        {/* Main Content */}
        <Grid item xs={12} md={8}>
          <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography variant="h5" gutterBottom>
                Transfer #{transfer.id}
                <Chip
                  label={transfer.transferType}
                  color={getTransferTypeColor(transfer.transferType)}
                  sx={{ ml: 2 }}
                  size="small"
                  variant="outlined"
                />
              </Typography>
            </Box>
            <Divider sx={{ my: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  From Zone
                </Typography>
                <Typography variant="body1">
                  {getZoneNameFromZone(transfer.fromWarehouseZone) || 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  To Zone
                </Typography>
                <Typography variant="body1">
                  {transfer.toWarehouseZone ? getZoneNameFromZone(transfer.toWarehouseZone) : 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Total Quantity Transferred
                </Typography>
                <Chip
                  label={getTotalQuantityTransferred()}
                  color="primary"
                  size="small"
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Total Items
                </Typography>
                <Typography variant="body1">{transfer.inventoryTransferItems.length}</Typography>
              </Grid>
            </Grid>
          </Paper>

          <InventoryTransferItemDetails items={transfer.inventoryTransferItems} />
        </Grid>

        {/* Sidebar */}
        <Grid item xs={12} md={4}>
          <ExportDetails
            exports={transfer.inventoryTransferExports}
            showSku={true}
            getSkuForItem={itemId =>
              transfer.inventoryTransferItems.find(item => item.id === itemId)?.sku
            }
          />

          <AuditInformation
            createdByUser={transfer.createdByUser}
            createdByUserId={transfer.createdByUserId}
            modifiedByUser={transfer.modifiedByUser}
            modifiedByUserId={transfer.modifiedByUserId}
            created={transfer.created}
            modified={transfer.modified}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default InventoryTransferDetails;
